import { converseApi } from '@/features/messaging';
import { profileApi } from '@/features/profile-management';
import { getStorageUrl } from '@/shared/lib';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { BlockMemberButton } from '@/widgets/member-block';
import { ProfilePhoto } from '@/widgets/photo-lightbox';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import {
  ArrowLeft,
  Baby,
  Briefcase,
  Calendar,
  CalendarCheck,
  CalendarX,
  Car,
  CheckCircle,
  Cigarette,
  Clock,
  Eye,
  GraduationCap,
  Heart,
  Home,
  Languages,
  Loader2,
  Lock,
  MapPin,
  MessageCircle,
  Moon,
  Palette,
  Ruler,
  Scissors,
  Star,
  Unlock,
  User,
  UserCheck,
  UserPlus,
  UserX,
  Users,
  XCircle,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

// Helper component to display a profile field
function ProfileField({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string | undefined | null;
}) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-3 px-4 rounded-lg bg-primary-surface/50 hover:bg-primary-surface transition-colors">
      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className="font-medium break-words mt-0.5">{value}</p>
      </div>
    </div>
  );
}

export default function ProfileDetailPage() {
  const { nodeId = '' } = useParams<{ nodeId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslate();

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile', nodeId],
    queryFn: () => profileApi.getProfile(Number.parseInt(nodeId)),
  });

  // Fetch full status (trust + meeting) between current user and this profile
  const { data: fullStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['fullStatus', nodeId],
    queryFn: () => profileApi.getFullStatus(Number.parseInt(nodeId)),
    enabled: !!profile, // Only fetch after profile is loaded
  });

  // Fetch private profile fields when trusted
  const { data: privateFields, isLoading: privateFieldsLoading } = useQuery({
    queryKey: ['privateProfile', nodeId],
    queryFn: () => profileApi.getPrivateProfile(Number.parseInt(nodeId)),
    enabled: !!fullStatus?.trust.isTrusted, // Only fetch when trusted
  });

  // Send trust request mutation
  const sendTrustRequestMutation = useMutation({
    mutationFn: () => profileApi.sendTrustRequest(Number.parseInt(nodeId)),
    onSuccess: () => {
      toast.success(t('profile.trustRequestSent'));
      queryClient.invalidateQueries({ queryKey: ['fullStatus', nodeId] });
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  // Accept trust request mutation
  const acceptTrustMutation = useMutation({
    mutationFn: () => profileApi.acceptTrustRequest(Number.parseInt(nodeId)),
    onSuccess: () => {
      toast.success(t('profile.trustAccepted'));
      queryClient.invalidateQueries({ queryKey: ['fullStatus', nodeId] });
      queryClient.invalidateQueries({ queryKey: ['profile', nodeId] });
      queryClient.invalidateQueries({ queryKey: ['privateProfile', nodeId] });
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  // Refuse trust request mutation
  const refuseTrustMutation = useMutation({
    mutationFn: () => profileApi.refuseTrustRequest(Number.parseInt(nodeId)),
    onSuccess: () => {
      toast.success(t('profile.trustRefused'));
      queryClient.invalidateQueries({ queryKey: ['fullStatus', nodeId] });
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  // Send meeting request mutation
  const sendMeetingRequestMutation = useMutation({
    mutationFn: () => profileApi.sendMeetingRequest(Number.parseInt(nodeId)),
    onSuccess: () => {
      toast.success(t('profile.meetingRequestSent'));
      queryClient.invalidateQueries({ queryKey: ['fullStatus', nodeId] });
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  // Accept meeting request mutation
  const acceptMeetingMutation = useMutation({
    mutationFn: () => profileApi.acceptMeetingRequest(Number.parseInt(nodeId)),
    onSuccess: () => {
      toast.success(t('profile.meetingAccepted'));
      queryClient.invalidateQueries({ queryKey: ['fullStatus', nodeId] });
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  // Refuse meeting request mutation
  const refuseMeetingMutation = useMutation({
    mutationFn: () => profileApi.refuseMeetingRequest(Number.parseInt(nodeId)),
    onSuccess: () => {
      toast.success(t('profile.meetingRefused'));
      queryClient.invalidateQueries({ queryKey: ['fullStatus', nodeId] });
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  const messageMutation = useMutation({
    mutationFn: () => converseApi.createConverse(Number.parseInt(nodeId)),
    onSuccess: (converse) => {
      navigate(`/chat/${converse.id}`);
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  // Determine which trust action buttons to show
  const getTrustButtonState = () => {
    if (statusLoading || !fullStatus) {
      return 'loading';
    }
    const trustStatus = fullStatus.trust;
    if (trustStatus.isTrusted) {
      return 'trusted';
    }
    if (trustStatus.hasReceivedRequest) {
      return 'received';
    }
    if (trustStatus.hasSentRequest) {
      return 'sent';
    }
    if (trustStatus.isRefused) {
      return 'refused';
    }
    return 'none';
  };

  // Determine which meeting action buttons to show
  const getMeetingButtonState = () => {
    if (statusLoading || !fullStatus) {
      return 'loading';
    }
    const meetingStatus = fullStatus.meeting;
    if (meetingStatus.isMeetingAccepted) {
      return 'accepted';
    }
    if (meetingStatus.hasReceivedRequest) {
      return 'received';
    }
    if (meetingStatus.hasSentRequest) {
      return 'sent';
    }
    if (meetingStatus.isRefused) {
      return 'refused';
    }
    return 'none';
  };

  const trustButtonState = getTrustButtonState();
  const meetingButtonState = getMeetingButtonState();

  // Get translated labels for profile fields
  const getLabel = (category: string, value: number | undefined): string => {
    if (value === undefined || value === null) return t('profile.notSpecified');
    return t(`profile.${category}.${value}`, { defaultValue: t('profile.notSpecified') });
  };

  const _getMultiLabels = (category: string, values: string | undefined): string => {
    if (!values) return '';
    return values
      .split(',')
      .map((v) => t(`profile.${category}.${v.trim()}`, { defaultValue: '' }))
      .filter(Boolean)
      .join(', ');
  };

  const getBooleanLabel = (value: number | undefined): string => {
    if (value === undefined || value === null) return t('profile.notSpecified');
    return t(`profile.boolean.${value}`, { defaultValue: t('profile.notSpecified') });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" asChild className="hover:bg-primary-surface">
          <Link to="/profiles">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('profile.backToDiscover')}
          </Link>
        </Button>
        <div className="grid gap-6 lg:grid-cols-3">
          <Card className="lg:col-span-1">
            <CardContent className="p-6">
              <Skeleton className="h-72 w-full rounded-xl mb-6" />
              <div className="text-center space-y-2">
                <Skeleton className="h-8 w-3/4 mx-auto" />
                <Skeleton className="h-4 w-1/2 mx-auto" />
              </div>
              <div className="mt-6 space-y-3">
                <Skeleton className="h-11 w-full" />
                <Skeleton className="h-11 w-full" />
              </div>
            </CardContent>
          </Card>
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="h-12 w-full rounded-lg" />
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-32" />
              </CardHeader>
              <CardContent className="space-y-4">
                <Skeleton className="h-24 w-full" />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-32" />
              </CardHeader>
              <CardContent className="space-y-4">
                <Skeleton className="h-24 w-full" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-16">
        <div className="h-20 w-20 mx-auto bg-primary-surface rounded-full flex items-center justify-center mb-6">
          <User className="h-10 w-10 text-primary" />
        </div>
        <h2 className="font-display text-2xl font-semibold mb-2">{t('profile.notFound')}</h2>
        <p className="text-muted-foreground mb-6 max-w-sm mx-auto">{t('profile.unavailable')}</p>
        <Button asChild>
          <Link to="/profiles">{t('profile.backToDiscover')}</Link>
        </Button>
      </div>
    );
  }

  // Format last activity date
  const formatLastActivity = (dateStr: string | undefined) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      <Button variant="ghost" asChild className="hover:bg-primary-surface">
        <Link to="/profiles">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('profile.backToDiscover')}
        </Link>
      </Button>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Profile Photo & Actions */}
        <Card className="lg:col-span-1 overflow-hidden">
          <CardContent className="p-0">
            {/* Photo section */}
            <ProfilePhoto
              src={getStorageUrl(profile.avatarPath)}
              alt={profile.username}
              fallback={profile.username?.charAt(0).toUpperCase() || 'U'}
            >
              {/* Online status */}
              {profile.isOnline && (
                <Badge className="absolute top-3 right-3 bg-success text-white border-0">
                  {t('profile.online')}
                </Badge>
              )}

              {/* Star rating */}
              {Number(profile.note) > 0 && (
                <div className="absolute top-3 left-3 flex bg-white/90 backdrop-blur-sm rounded-lg px-2 py-1 shadow-sm">
                  {Array.from({ length: Number(profile.note) }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-accent fill-accent" />
                  ))}
                </div>
              )}

              {/* Photo verified badge */}
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                <Badge
                  className={`${
                    profile.isPhotoVerified
                      ? 'bg-success/90 hover:bg-success'
                      : 'bg-destructive/90 hover:bg-destructive'
                  } border-0`}
                >
                  {profile.isPhotoVerified ? (
                    <>
                      <CheckCircle className="h-3 w-3 mr-1" />
                      {t('profile.photoVerified')}
                    </>
                  ) : (
                    <>
                      <XCircle className="h-3 w-3 mr-1" />
                      {t('profile.photoNotVerified')}
                    </>
                  )}
                </Badge>
              </div>
            </ProfilePhoto>

            {/* Profile info */}
            <div className="p-6">
              <div className="text-center">
                <h1 className="font-display text-2xl font-semibold">{profile.username}</h1>
                <p className="text-muted-foreground mt-1">
                  {Number(profile.age) > 0 && `${profile.age} ans`}
                </p>
                {profile.city && (
                  <div className="flex items-center justify-center mt-2 text-muted-foreground">
                    <MapPin className="h-4 w-4 mr-1 text-primary" />
                    {profile.city}
                    {profile.region && ` (${profile.region})`}
                  </div>
                )}

                {/* Nationality & Origin flags */}
                {(Number(profile.nationality) > 0 || Number(profile.origin) > 0) && (
                  <div className="flex items-center justify-center gap-4 mt-4 flex-wrap">
                    {Number(profile.nationality) > 0 && (
                      <div className="flex items-center gap-1.5 text-sm bg-primary-surface px-3 py-1.5 rounded-full">
                        <img
                          src={`/flags/${profile.nationality}.png`}
                          alt={profile.nationalityLabel ?? ''}
                          width={20}
                          height={14}
                          className="inline-block rounded-sm"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        <span className="text-xs font-medium">
                          {t('profile.fields.nationality')} : {profile.nationalityLabel}
                        </span>
                      </div>
                    )}
                    {Number(profile.origin) > 0 && profile.origin !== profile.nationality && (
                      <div className="flex items-center gap-1.5 text-sm bg-accent/10 px-3 py-1.5 rounded-full">
                        <img
                          src={`/flags/${profile.origin}.png`}
                          alt={profile.originLabel ?? ''}
                          width={20}
                          height={14}
                          className="inline-block rounded-sm"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        <span className="text-xs font-medium">
                          {t('profile.fields.origin')} : {profile.originLabel}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Last activity */}
                {profile.lastActivity && (
                  <div className="flex items-center justify-center mt-4 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4 mr-1.5" />
                    {t('profile.lastConnection')}: {formatLastActivity(profile.lastActivity)}
                  </div>
                )}
              </div>

              <div className="mt-6 space-y-3">
                {/* Trust request buttons based on status */}
                {trustButtonState === 'loading' && (
                  <Button className="w-full" disabled>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common.loading')}
                  </Button>
                )}

                {trustButtonState === 'none' && (
                  <Button
                    className="w-full"
                    onClick={() => sendTrustRequestMutation.mutate()}
                    disabled={sendTrustRequestMutation.isPending}
                  >
                    {sendTrustRequestMutation.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <UserPlus className="mr-2 h-4 w-4" />
                    )}
                    {t('profile.trustRequest')}
                  </Button>
                )}

                {trustButtonState === 'sent' && (
                  <Button className="w-full" variant="secondary" disabled>
                    <Clock className="mr-2 h-4 w-4" />
                    {t('profile.trustRequestPending')}
                  </Button>
                )}

                {trustButtonState === 'received' && (
                  <>
                    <Button
                      className="w-full"
                      onClick={() => acceptTrustMutation.mutate()}
                      disabled={acceptTrustMutation.isPending}
                    >
                      {acceptTrustMutation.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <UserCheck className="mr-2 h-4 w-4" />
                      )}
                      {t('profile.trustAccept')}
                    </Button>
                    <Button
                      className="w-full"
                      variant="outline"
                      onClick={() => refuseTrustMutation.mutate()}
                      disabled={refuseTrustMutation.isPending}
                    >
                      {refuseTrustMutation.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <UserX className="mr-2 h-4 w-4" />
                      )}
                      {t('profile.trustRefuse')}
                    </Button>
                  </>
                )}

                {trustButtonState === 'trusted' && (
                  <Button className="w-full bg-success hover:bg-success/90" disabled>
                    <Unlock className="mr-2 h-4 w-4" />
                    {t('profile.trustShared')}
                  </Button>
                )}

                {trustButtonState === 'refused' && (
                  <Button className="w-full" variant="secondary" disabled>
                    <Lock className="mr-2 h-4 w-4" />
                    {t('profile.trustRefusedStatus')}
                  </Button>
                )}

                {/* Meeting (rendez-vous) actions — only once profiles are shared */}
                {trustButtonState === 'trusted' && (
                  <>
                    {meetingButtonState === 'none' && (
                      <Button
                        className="w-full"
                        onClick={() => sendMeetingRequestMutation.mutate()}
                        disabled={sendMeetingRequestMutation.isPending}
                      >
                        {sendMeetingRequestMutation.isPending ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Calendar className="mr-2 h-4 w-4" />
                        )}
                        {t('profile.sendMeetingRequest')}
                      </Button>
                    )}

                    {meetingButtonState === 'sent' && (
                      <Button className="w-full" variant="secondary" disabled>
                        <Clock className="mr-2 h-4 w-4" />
                        {t('profile.meetingRequestPending')}
                      </Button>
                    )}

                    {meetingButtonState === 'received' && (
                      <>
                        <Button
                          className="w-full"
                          onClick={() => acceptMeetingMutation.mutate()}
                          disabled={acceptMeetingMutation.isPending}
                        >
                          {acceptMeetingMutation.isPending ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <CalendarCheck className="mr-2 h-4 w-4" />
                          )}
                          {t('profile.meetingAccept')}
                        </Button>
                        <Button
                          className="w-full"
                          variant="outline"
                          onClick={() => refuseMeetingMutation.mutate()}
                          disabled={refuseMeetingMutation.isPending}
                        >
                          {refuseMeetingMutation.isPending ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <CalendarX className="mr-2 h-4 w-4" />
                          )}
                          {t('profile.meetingRefuse')}
                        </Button>
                      </>
                    )}

                    {meetingButtonState === 'accepted' && (
                      <Button className="w-full bg-success hover:bg-success/90" disabled>
                        <CalendarCheck className="mr-2 h-4 w-4" />
                        {t('profile.meetingAccepted')}
                      </Button>
                    )}

                    {meetingButtonState === 'refused' && (
                      <Button className="w-full" variant="secondary" disabled>
                        <CalendarX className="mr-2 h-4 w-4" />
                        {t('profile.meetingRefused')}
                      </Button>
                    )}
                  </>
                )}

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => messageMutation.mutate()}
                  disabled={messageMutation.isPending}
                >
                  {messageMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <MessageCircle className="mr-2 h-4 w-4" />
                  )}
                  {t('profile.sendMessage')}
                </Button>

                <BlockMemberButton
                  nodeId={Number.parseInt(nodeId)}
                  username={profile.username}
                  onBlocked={() => navigate('/profiles')}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profile Details with Tabs */}
        <div className="lg:col-span-2">
          <Tabs defaultValue="public" className="w-full">
            <TabsList className="grid w-full grid-cols-2 h-12 p-1">
              <TabsTrigger
                value="public"
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                {t('profile.publicProfile')}
              </TabsTrigger>
              <TabsTrigger
                value="private"
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                {t('profile.privateProfile')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="public" className="space-y-6 mt-6">
              {/* Description */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-display flex items-center gap-2">
                    <User className="h-5 w-5 text-primary" />
                    {t('profile.sections.description')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {profile.description ? (
                    <p className="whitespace-pre-wrap leading-relaxed">{profile.description}</p>
                  ) : (
                    <p className="text-muted-foreground italic">{t('profile.noBio')}</p>
                  )}
                </CardContent>
              </Card>

              {/* Location */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-display flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    {t('profile.sections.location')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <ProfileField
                      icon={MapPin}
                      label={t('profile.fields.city')}
                      value={profile.city}
                    />
                    <ProfileField
                      icon={MapPin}
                      label={t('profile.fields.region')}
                      value={profile.region}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Studies and Profession */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-display flex items-center gap-2">
                    <GraduationCap className="h-5 w-5 text-primary" />
                    {t('profile.sections.studies')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <ProfileField
                      icon={GraduationCap}
                      label={t('profile.fields.studyLevel')}
                      value={getLabel('studyLevel', profile.studyLevel)}
                    />
                    <ProfileField
                      icon={Briefcase}
                      label={t('profile.fields.profession')}
                      value={profile.profession}
                    />
                    <ProfileField
                      icon={Briefcase}
                      label={t('profile.fields.nodeBusiness')}
                      value={getLabel('nodeBusiness', profile.nodeBusiness)}
                    />
                    <ProfileField
                      icon={GraduationCap}
                      label={t('profile.fields.isStudyLevelMatter')}
                      value={getBooleanLabel(profile.isStudyLevelMatter)}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Personal Situation */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-display flex items-center gap-2">
                    <Heart className="h-5 w-5 text-primary" />
                    {t('profile.sections.situation')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <ProfileField
                      icon={Heart}
                      label={t('profile.fields.maritalStatus')}
                      value={getLabel('maritalStatus', profile.maritalStatus)}
                    />
                    <ProfileField
                      icon={Baby}
                      label={t('profile.fields.children')}
                      value={getLabel('children', profile.children)}
                    />
                    <ProfileField
                      icon={Home}
                      label={t('profile.fields.livingPlace')}
                      value={getLabel('livingPlace', profile.livingPlace)}
                    />
                    <ProfileField
                      icon={User}
                      label={t('profile.fields.administrativeState')}
                      value={getLabel('administrativeState', profile.administrativeState)}
                    />
                    <ProfileField
                      icon={Users}
                      label={t('profile.fields.familyValues')}
                      value={getLabel('familyValues', profile.familyValues)}
                    />
                    <ProfileField
                      icon={Heart}
                      label={t('profile.fields.peerPast')}
                      value={getLabel('peerPast', profile.peerPast)}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Religious Practice */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-display flex items-center gap-2">
                    <Moon className="h-5 w-5 text-primary" />
                    {t('profile.sections.religion')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <ProfileField
                      icon={Moon}
                      label={t('profile.fields.faith')}
                      value={getLabel('faith', profile.faith)}
                    />
                    <ProfileField
                      icon={Moon}
                      label={t('profile.fields.minaj')}
                      value={getLabel('minaj', profile.minaj)}
                    />
                    {profile.veil !== undefined && profile.veil !== null && (
                      <ProfileField
                        icon={User}
                        label={t('profile.fields.veil')}
                        value={getLabel('veil', profile.veil)}
                      />
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Appearance */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-display flex items-center gap-2">
                    <Eye className="h-5 w-5 text-primary" />
                    {t('profile.sections.appearance')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <ProfileField
                      icon={Palette}
                      label={t('profile.fields.shape')}
                      value={getLabel('shape', profile.shape)}
                    />
                    {Number(profile.size) > 0 && (
                      <ProfileField
                        icon={Ruler}
                        label={t('profile.fields.size')}
                        value={`${profile.size} cm`}
                      />
                    )}
                    <ProfileField
                      icon={Eye}
                      label={t('profile.fields.eye')}
                      value={getLabel('eye', profile.eye)}
                    />
                    <ProfileField
                      icon={Scissors}
                      label={t('profile.fields.hair')}
                      value={getLabel('hair', profile.hair)}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Languages & Personality */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-display flex items-center gap-2">
                    <Languages className="h-5 w-5 text-primary" />
                    {t('profile.sections.languages')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {profile.spokenLanguages && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-3 uppercase tracking-wide">
                          {t('profile.fields.spokenLanguages')}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {profile.spokenLanguages.split(',').map((langId) => (
                            <Badge
                              key={langId}
                              className="bg-primary-surface text-primary border-0 px-3 py-1"
                            >
                              {t(`profile.spokenLanguages.${langId.trim()}`, {
                                defaultValue: langId,
                              })}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {profile.lifePersonality && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-3 uppercase tracking-wide">
                          {t('profile.fields.lifePersonality')}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {profile.lifePersonality.split(',').map((traitId) => (
                            <Badge
                              key={traitId}
                              className="bg-accent/10 text-accent border-0 px-3 py-1"
                            >
                              {t(`profile.lifePersonality.${traitId.trim()}`, {
                                defaultValue: traitId,
                              })}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Other Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-display flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-primary" />
                    {t('profile.about')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <ProfileField
                      icon={Car}
                      label={t('profile.fields.licence')}
                      value={getBooleanLabel(profile.licence)}
                    />
                    <ProfileField
                      icon={Cigarette}
                      label={t('profile.fields.smoke')}
                      value={getBooleanLabel(profile.smoke)}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="private" className="mt-6 space-y-6">
              {/* Show private profile content if trusted, otherwise show request UI */}
              {trustButtonState === 'trusted' ? (
                <>
                  {/* Meeting Request Card */}
                  <Card className="border-primary/20">
                    <CardHeader>
                      <CardTitle className="font-display flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-primary" />
                        {t('profile.meetingRequest')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {meetingButtonState === 'loading' && (
                        <div className="flex justify-center py-4">
                          <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                      )}

                      {meetingButtonState === 'none' && (
                        <div className="text-center py-4">
                          <p className="text-muted-foreground mb-4">
                            {t('profile.meetingRequestDescription')}
                          </p>
                          <Button
                            onClick={() => sendMeetingRequestMutation.mutate()}
                            disabled={sendMeetingRequestMutation.isPending}
                          >
                            {sendMeetingRequestMutation.isPending ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Calendar className="mr-2 h-4 w-4" />
                            )}
                            {t('profile.sendMeetingRequest')}
                          </Button>
                        </div>
                      )}

                      {meetingButtonState === 'sent' && (
                        <div className="text-center py-4">
                          <p className="text-muted-foreground mb-4">
                            {t('profile.meetingRequestWaiting')}
                          </p>
                          <Badge variant="secondary" className="text-sm py-2 px-4">
                            <Clock className="mr-2 h-4 w-4" />
                            {t('profile.meetingRequestPending')}
                          </Badge>
                        </div>
                      )}

                      {meetingButtonState === 'received' && (
                        <div className="text-center py-4">
                          <p className="text-muted-foreground mb-4">
                            {t('profile.meetingRequestReceivedDescription')}
                          </p>
                          <div className="flex gap-3 justify-center">
                            <Button
                              onClick={() => acceptMeetingMutation.mutate()}
                              disabled={acceptMeetingMutation.isPending}
                            >
                              {acceptMeetingMutation.isPending ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <CalendarCheck className="mr-2 h-4 w-4" />
                              )}
                              {t('profile.meetingAccept')}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => refuseMeetingMutation.mutate()}
                              disabled={refuseMeetingMutation.isPending}
                            >
                              {refuseMeetingMutation.isPending ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <CalendarX className="mr-2 h-4 w-4" />
                              )}
                              {t('profile.meetingRefuse')}
                            </Button>
                          </div>
                        </div>
                      )}

                      {meetingButtonState === 'accepted' && (
                        <div className="text-center py-4">
                          <div className="h-16 w-16 mx-auto bg-success/10 rounded-full flex items-center justify-center mb-4">
                            <CalendarCheck className="h-8 w-8 text-success" />
                          </div>
                          <p className="text-success font-medium">
                            {t('profile.meetingAcceptedStatus')}
                          </p>
                        </div>
                      )}

                      {meetingButtonState === 'refused' && (
                        <div className="text-center py-4">
                          <p className="text-muted-foreground">
                            {t('profile.meetingRefusedStatus')}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Private Profile Fields Card */}
                  <Card className="border-success/20 bg-success/5">
                    <CardHeader>
                      <CardTitle className="font-display flex items-center gap-2 text-success">
                        <Unlock className="h-5 w-5" />
                        {t('profile.privateProfileUnlocked')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {privateFieldsLoading ? (
                        <div className="flex justify-center py-8">
                          <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                      ) : privateFields && privateFields.length > 0 ? (
                        <div className="grid gap-3 sm:grid-cols-2">
                          {privateFields.map((field, index) => {
                            const fieldLabel = t(`profile.privateFields.${field.name}.label`, {
                              defaultValue: field.name,
                            });
                            const fieldValue =
                              field.value !== undefined && field.value !== null
                                ? t(`profile.privateFields.${field.name}.values.${field.value}`, {
                                    defaultValue: String(field.value),
                                  })
                                : t('profile.notSpecified');
                            return (
                              <div
                                key={index}
                                className="flex items-start gap-3 py-3 px-4 rounded-lg bg-white/50 hover:bg-white/80 transition-colors"
                              >
                                <div className="min-w-0">
                                  <p className="text-xs text-muted-foreground uppercase tracking-wide">
                                    {fieldLabel}
                                  </p>
                                  <p className="font-medium break-words mt-0.5">{fieldValue}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-center py-4">
                          {t('profile.noPrivateFields')}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Card className="border-primary/20">
                  <CardContent className="p-8 text-center">
                    <div className="h-20 w-20 mx-auto bg-primary-surface rounded-full flex items-center justify-center mb-6">
                      <Lock className="h-10 w-10 text-primary" />
                    </div>
                    <h3 className="font-display text-xl font-semibold mb-2">
                      {t('profile.privateProfile')}
                    </h3>

                    {/* No trust relationship - can send request */}
                    {trustButtonState === 'none' && (
                      <>
                        <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                          {t('profile.privateProfileDescription')}
                        </p>
                        <Button
                          onClick={() => sendTrustRequestMutation.mutate()}
                          disabled={sendTrustRequestMutation.isPending}
                        >
                          {sendTrustRequestMutation.isPending ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <UserPlus className="mr-2 h-4 w-4" />
                          )}
                          {t('profile.trustRequest')}
                        </Button>
                      </>
                    )}

                    {/* Request already sent - waiting for response */}
                    {trustButtonState === 'sent' && (
                      <>
                        <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                          {t('profile.trustRequestWaiting')}
                        </p>
                        <Badge variant="secondary" className="text-sm py-2 px-4">
                          <Clock className="mr-2 h-4 w-4" />
                          {t('profile.trustRequestPending')}
                        </Badge>
                      </>
                    )}

                    {/* Received a request from this user - can accept/refuse */}
                    {trustButtonState === 'received' && (
                      <>
                        <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                          {t('profile.trustRequestReceivedDescription')}
                        </p>
                        <div className="flex gap-3 justify-center">
                          <Button
                            onClick={() => acceptTrustMutation.mutate()}
                            disabled={acceptTrustMutation.isPending}
                          >
                            {acceptTrustMutation.isPending ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <UserCheck className="mr-2 h-4 w-4" />
                            )}
                            {t('profile.trustAccept')}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => refuseTrustMutation.mutate()}
                            disabled={refuseTrustMutation.isPending}
                          >
                            {refuseTrustMutation.isPending ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <UserX className="mr-2 h-4 w-4" />
                            )}
                            {t('profile.trustRefuse')}
                          </Button>
                        </div>
                      </>
                    )}

                    {/* Request was refused */}
                    {trustButtonState === 'refused' && (
                      <p className="text-muted-foreground max-w-sm mx-auto">
                        {t('profile.trustRefusedDescription')}
                      </p>
                    )}

                    {/* Loading state */}
                    {trustButtonState === 'loading' && (
                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
